/**
 * Regulatory reference catalog: each row cites an official or peer-reviewed primary URL.
 * Summaries are for navigation only — verify final compliance language on the linked source.
 */

import { CCME_CWQG_TEMPERATURE } from "../config/dataSources";

export const REGULATORY_ROWS = [
  {
    id: "epa-r10-guidance",
    jurisdictionLabel: "🇺🇸 US (EPA Region 10)",
    jurisdictionCode: "US",
    type: "BINDING",
    instrument: "EPA Region 10 — Guidance for Pacific Northwest State and Tribal Temperature Water Quality Standards",
    thresholds: "7-DADMax: 13°C spawning, 16°C rearing, 18°C migration; incremental warming cap 0.3°C when waters at/near criteria (adopted in state WQS).",
    provisionSummary: "Temperature criteria framework for Pacific Northwest waters; incremental limits for human-caused warming.",
    sourceUrl: "https://www.epa.gov/sites/default/files/2015-10/documents/910b03002.pdf",
    gap: "Sealed underwater heat exchange vs. classic “discharge” point source may require agency interpretation.",
    citations: "U.S. EPA Region 10, 2003. Guidance for Pacific Northwest State and Tribal Temperature Water Quality Standards (EPA 910-B-03-002).",
    keywords: ["epa", "region 10", "7-dadmax", "incremental", "0.3", "thermal", "temperature", "spawning"],
  },
  {
    id: "cwa-316a",
    jurisdictionLabel: "🇺🇸 US Federal",
    jurisdictionCode: "US",
    type: "BINDING",
    instrument: "Clean Water Act §316(a) — alternative thermal limits",
    thresholds: "NPDES thermal limits may be relaxed where discharger demonstrates protection of balanced indigenous populations (BIP).",
    provisionSummary: "Thermal variance pathway tied to biological demonstration; permit renewal typically on 5-year cycles.",
    sourceUrl: "https://www.ecfr.gov/current/title-40/chapter-I/subchapter-D/part-125/subpart-H",
    gap: "Designed around intake/outfall cooling water; applicability to sealed capsules / passive exchange is unsettled.",
    citations: "33 U.S.C. §1326(a); 40 C.F.R. §§125.70–125.73.",
    keywords: ["316a", "cwa", "variance", "thermal", "npdes", "bip"],
  },
  {
    id: "cwa-316b",
    jurisdictionLabel: "🇺🇸 US Federal",
    jurisdictionCode: "US",
    type: "BINDING",
    instrument: "Clean Water Act §316(b) — cooling water intake structures",
    thresholds: "BTA requirements for facilities withdrawing ≥2 MGD (impingement standards; entrainment reduction requirements).",
    provisionSummary: "National standards for cooling water intakes; relevant where facilities use significant ambient water exchange.",
    sourceUrl: "https://www.ecfr.gov/current/title-40/chapter-I/subchapter-D/part-125/subpart-I",
    gap: "May not apply if there is no active cooling-water intake above regulatory thresholds.",
    citations: "33 U.S.C. §1326(b); 40 C.F.R. §§125.90–125.99.",
    keywords: ["316b", "intake", "cooling", "entrainment", "impingement", "cwa"],
  },
  {
    id: "ccme-marine-temp",
    jurisdictionLabel: "🇨🇦 Canada (CCME)",
    jurisdictionCode: "CA",
    type: "ADVISORY",
    instrument: "CCME — Canadian Water Quality Guidelines: Temperature (marine)",
    thresholds: "±1°C change from natural conditions at any time/location/depth; max rate of change 0.5°C/h where guideline applies.",
    provisionSummary: "National science guideline informing provincial/territorial permits; not a statute by itself.",
    sourceUrl: CCME_CWQG_TEMPERATURE,
    gap: "No Canada-wide thermal “variance” parallel to CWA §316(a); provincial instruments carry enforcement.",
    citations: "Canadian Council of Ministers of the Environment. Canadian Water Quality Guidelines: Temperature (marine aquatic life).",
    keywords: ["ccme", "1°c", "marine", "canada", "guideline", "thermal"],
  },
  {
    id: "fisheries-act-36",
    jurisdictionLabel: "🇨🇦 Canada (Federal)",
    jurisdictionCode: "CA",
    type: "BINDING",
    instrument: "Fisheries Act — s.34–36 (deleterious substances)",
    thresholds:
      "Prohibition on deposit of deleterious substances in water frequented by fish. Public legal summaries have cited very large first-offense fines (e.g. $6M narratives); verify current sentencing and guideline tables on official Justice / DFO materials.",
    provisionSummary: "Strict federal fish habitat protection; heated water may be treated as deleterious depending on facts.",
    sourceUrl: "https://laws-lois.justice.gc.ca/eng/acts/F-14/",
    gap: "No thermal variance pathway comparable to US §316(a); novel subsea heat rejection needs federal/provincial alignment.",
    citations: "Fisheries Act, R.S.C. 1985, c. F-14, ss. 34–36.",
    keywords: ["fisheries", "§36", "deleterious", "canada", "fish", "fine", "thermal"],
  },
  {
    id: "wa-wac-173",
    jurisdictionLabel: "🇺🇸 Washington",
    jurisdictionCode: "US",
    type: "BINDING",
    instrument: "WAC 173-201A — Water quality standards for surface waters",
    thresholds: "Marine 7-DADMax bands; cumulative human-caused warming capped at 0.3°C when criteria are at/near exceeded; plume/fish exposure constraints in rule text.",
    provisionSummary: "State temperature and mixing-zone requirements adopted under Clean Water Act framework.",
    sourceUrl: "https://app.leg.wa.gov/wac/default.aspx?cite=173-201A",
    gap: "Classification of sealed subsea data-center heat exchange as a regulated discharge may require Ecology determination.",
    citations: "Washington Administrative Code Chapter 173-201A.",
    keywords: ["washington", "wac", "173-201a", "0.3", "puget", "thermal"],
  },
  {
    id: "ca-thermal-plan",
    jurisdictionLabel: "🇺🇸 California",
    jurisdictionCode: "US",
    type: "BINDING",
    instrument: "California Thermal Plan (SWRCB Resolution 75-89)",
    thresholds: "Enclosed bays (e.g. San Francisco Bay): thermal discharge >4°F (~2.2°C) above natural prohibited; coastal ocean: ≤4°F at shoreline/substrate/surface beyond 1,000 ft (see plan text).",
    provisionSummary: "State board policy incorporated into NPDES/WDR conditions for thermal discharges.",
    sourceUrl: "https://www.waterboards.ca.gov/water_issues/programs/ocean/thermal.html",
    gap: "Novel underwater infrastructure may implicate BCDC, sanctuary, and thermal plan layers concurrently.",
    citations: "State Water Resources Control Board Resolution No. 75-89 and supporting policies.",
    keywords: ["california", "thermal plan", "75-89", "2.2", "4°f", "san francisco", "bay"],
  },
  {
    id: "npdes-overview",
    jurisdictionLabel: "🇺🇸 US Federal",
    jurisdictionCode: "US",
    type: "BINDING",
    instrument: "NPDES program — technology & water-quality based effluent limits",
    thresholds: "Permit writers set TBELs and more stringent WQBELs including heat where narrative/numeric criteria apply.",
    provisionSummary: "Primary federal vehicle for thermal limits at point sources discharging to waters of the US.",
    sourceUrl: "https://www.epa.gov/npdes",
    gap: "Permit terms are site-specific; underwater DC projects need early NPDES scoping with Regions 9/10.",
    citations: "Clean Water Act §402; 40 C.F.R. parts 122–125 (general NPDES program requirements).",
    keywords: ["npdes", "permit", "effluent", "thermal", "epa", "discharge"],
  },
  {
    id: "oregon-thermal",
    jurisdictionLabel: "🇺🇸 Oregon",
    jurisdictionCode: "US",
    type: "BINDING",
    instrument: "Oregon — temperature water quality standards (incremental warming)",
    thresholds: "Cold-water biota contexts often reference 0.3°C incremental protection (align with EPA Region 10 framework); verify current OAR/DEQ adoption.",
    provisionSummary: "State standards implementing CWA temperature criteria on Oregon coastal and estuarine waters.",
    sourceUrl: "https://www.oregon.gov/deq/wq/pages/wqs-temperature.aspx",
    gap: "Site classification drives which temperature tiers apply.",
    citations: "Oregon Department of Environmental Quality water quality standards (temperature).",
    keywords: ["oregon", "deq", "incremental", "0.3", "thermal", "columbia"],
  },
  {
    id: "dfo-stats",
    jurisdictionLabel: "🇨🇦 Canada (DFO)",
    jurisdictionCode: "CA",
    type: "ADVISORY",
    instrument: "DFO — fisheries statistics & open data",
    thresholds: "Not a thermal limit; supports population/habitat context for impact screening.",
    provisionSummary: "Official fisheries statistics and datasets for regional abundance and management reference points.",
    sourceUrl: "https://www.dfo-mpo.gc.ca/stats/commercial/index-eng.htm",
    gap: "Use alongside provincial permits and Fisheries Act prohibitions.",
    citations: "Fisheries and Oceans Canada statistical publications and open datasets.",
    keywords: ["dfo", "fisheries", "statistics", "canada", "data"],
  },
];

/** Oceanic / underwater datacenter — water & thermal context (links only; no scraped text). */
export const WATER_DC_REGULATIONS = [
  {
    id: "epa-r10-npdes",
    title: "EPA Region 10 — NPDES & water division",
    summary: "Regional permits, guidance, and contacts for thermal limits in AK/ID/OR/WA/tribal waters.",
    sourceUrl: "https://www.epa.gov/aboutepa/epa-region-10-pacific-northwest",
    keywords: ["region 10", "npdes", "thermal", "permit"],
  },
  {
    id: "epa-thermal-plants",
    title: "EPA — Steam electric / thermal discharge context",
    summary: "Technical background on thermal discharges from power-generation analogues (screening analogies for plume modeling).",
    sourceUrl: "https://www.epa.gov/steam-electric-power-generating-effluent-guidelines",
    keywords: ["thermal", "plume", "power plant", "316"],
  },
  {
    id: "noaa-sst",
    title: "NOAA / Open-Meteo — live sea-surface temperature",
    summary: "Use live SST from marine weather APIs when screening mixing zones against ambient fields.",
    sourceUrl: "https://open-meteo.com/en/docs/marine-weather-api",
    keywords: ["noaa", "sst", "marine", "open-meteo", "screening"],
  },
  {
    id: "nanoos-explorer",
    title: "NANOOS — Visualization / data explorer",
    summary: "Pacific Northwest observatory assets for contextual ocean state alongside modeled plumes.",
    sourceUrl: "https://data.nanoos.org/",
    keywords: ["nanoos", "observations", "pacific northwest"],
  },
];

export function filterRegulatoryRows(rows, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => {
    const hay = [
      row.instrument,
      row.thresholds,
      row.provisionSummary,
      row.gap,
      row.citations,
      row.jurisdictionLabel,
      ...(row.keywords || []),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

export function filterWaterDcRows(rows, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => `${r.title} ${r.summary} ${r.keywords?.join(" ")}`.toLowerCase().includes(q));
}
